import { formatDateValue } from "../../utils/dateUtils";
import {
  applyMetricMultiplier,
  collectFormulaKeys,
  collectMetricKeys,
  selectMetricRows,
  stripNonNumericValues,
} from "../../utils/metricsUtils";
import { Checkbox, FormControlLabel, Paper } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import * as aggregates from "common/aggregates";
import * as formulae from "common/formulae";
import Metrics from "components/common/Metrics";
import * as labels from "config.labels";
import React from "react";
import { useMemo } from "react";
import { createPortal } from "react-dom";
import * as staticDataHooks from "store/hooks/staticDataHooks";

const context = "reportsV3 metrics";

const getStatusChartColours = (theme) => ({
  DRAFT: theme.brand.status.opportunity,
  SHELVED: theme.brand.status.closed,
  QUOTE_REQUESTED: theme.brand.status.inProgress,
  DECLINED_TO_QUOTE: theme.brand.status.closed,
  QUOTED: theme.brand.status.inProgress,
  BOUND: theme.brand.status.bound,
  NOT_TAKEN_UP: theme.brand.status.notTakenUp,
  LOST: theme.brand.status.closed,
});

const GENERATED_COLOURS = [
  "#1976d2",
  "#388e3c",
  "#d32f2f",
  "#7b1fa2",
  "#f57c00",
  "#0097a7",
  "#c2185b",
  "#512da8",
  "#00796b",
  "#e64a19",
  "#5d4037",
  "#455a64",
  "#afb42b",
  "#0288d1",
  "#689f38",
];

const buildEnumChartData = (config, data, colourOverrides) => {
  if (!config?.key || !Array.isArray(data) || data.length === 0) return null;
  const counts = {};
  for (const row of data) {
    const val = row[config.key];
    if (val != null && val !== "") {
      const key = String(val);
      counts[key] = (counts[key] || 0) + 1;
    }
  }
  if (Object.keys(counts).length === 0) return null;

  const optionMap = new Map();
  if (Array.isArray(config.options)) {
    for (const opt of config.options) {
      optionMap.set(String(opt.internalName), opt);
    }
  }

  const MAX_CHART_BARS = 6;
  let entries = Object.entries(counts);
  if (entries.length > MAX_CHART_BARS) {
    entries.sort((a, b) => b[1] - a[1]);
    entries = entries.slice(0, MAX_CHART_BARS);
  }

  let colourIndex = 0;
  return entries.map(([key, value]) => {
    const opt = optionMap.get(key);
    const label = opt?.displayName ?? key;
    let colour = colourOverrides?.[key] || opt?.foreground || null;
    if (!colour) {
      colour = GENERATED_COLOURS[colourIndex % GENERATED_COLOURS.length];
      colourIndex++;
    }
    return { label, value, colour };
  });
};

const hasColumnKey = (data, key) => {
  if (!Array.isArray(data) || data.length === 0) return false;
  return data.some(
    (row) =>
      row && Object.prototype.hasOwnProperty.call(row, key) && row[key] != null
  );
};

const hasRequiredMetricKeys = (config, data) => {
  if (config?.formula) {
    const keys = [...collectFormulaKeys(config.formula)];
    return keys.every((key) => hasColumnKey(data, key));
  }
  if (config?.key) {
    return hasColumnKey(data, config.key);
  }
  return true;
};

const calculateMetricValue = (config, data) => {
  try {
    if (!hasRequiredMetricKeys(config, data)) {
      return null;
    }
    if (config.formula) {
      return formulae.apply(config.formula, data, context);
    }
    if (config.method && config.key) {
      return aggregates.aggregatedByKey(
        config.method,
        config.key,
        data,
        context
      );
    }
    return null;
  } catch {
    return null;
  }
};

export const MetricsButton = ({
  metricsItems,
  data: rawData,
  metricsCollapsed,
  onToggle,
  disabled = false,
  isLoading = false,
  panelContainerRef,
  dateFormat,
  displayControlColumn,
}) => {
  const theme = useTheme();
  const translateLabels = staticDataHooks.useTranslator("uiLabels");
  const calculatedMetrics = useMemo(() => {
    if (isLoading || !rawData) {
      return [];
    }
    const data = stripNonNumericValues(
      selectMetricRows(rawData, displayControlColumn),
      collectMetricKeys(metricsItems)
    );

    return (metricsItems ?? []).flatMap((config) => {
      if (config.chartType === "enumButtons") {
        const colours = config.statusChart
          ? getStatusChartColours(theme)
          : undefined;
        const chartData = buildEnumChartData(config, data, colours);
        if (!chartData) return [];
        return [{ label: config.label, data: chartData }];
      }
      let value = calculateMetricValue(config, data);
      if (
        config.dataType === "date" &&
        value != null &&
        typeof value === "string"
      ) {
        value = formatDateValue(value, dateFormat || "DD/MM/YYYY");
      } else {
        value = applyMetricMultiplier(value, config.multiplier);
      }
      return [
        {
          label: config.label,
          value,
          prefix: config.prefix,
          suffix: config.suffix,
          dp: config.dp,
          tip: config.tip,
        },
      ];
    });
  }, [
    isLoading,
    rawData,
    metricsItems,
    dateFormat,
    theme,
    displayControlColumn,
  ]);

  const hasAnyMetrics = isLoading || calculatedMetrics.length > 0;
  const hasData = Array.isArray(rawData) && rawData.length > 0;

  const canRenderPanel =
    !metricsCollapsed && Boolean(panelContainerRef?.current) && hasData;

  if (!hasAnyMetrics) {
    return null;
  }
  return (
    <React.Fragment>
      <FormControlLabel
        control={
          <Checkbox
            size={"small"}
            checked={!metricsCollapsed}
            onChange={() => onToggle()}
            disabled={disabled}
            aria-label={"toggle metrics"}
          />
        }
        label={translateLabels(labels.METRICS_BUTTON_METRICS, {
          default: "Metrics",
        })}
        sx={{
          marginLeft: 0,
          marginRight: 0,
          "& .MuiFormControlLabel-label": {
            fontSize: "0.8125rem",
            fontWeight: 500,
          },
        }}
      />
      {canRenderPanel &&
        createPortal(
          <Paper
            elevation={0}
            sx={{
              mb: 2,
              backgroundColor: "grey.50",
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              overflow: "hidden",
              py: 1,
              px: 1,
            }}
          >
            <Metrics metrics={calculatedMetrics} />
          </Paper>,
          panelContainerRef.current
        )}
    </React.Fragment>
  );
};
export default MetricsButton;
